import get from 'lodash/get';
import {pathnameFromString} from '@utils/route/derivePathName';
import {Actions, MessagingActions, PageAttributesAndActions, PageProps} from '@components/IframeView/iframeview.types';
import Router from 'next/router';
import useNavigationStore from '@store/client/store.navigation';
import {SegmentTrackingAction, TRACKING_TYPES} from '@integrations/segment/webflow';

/**
 * Our action object. We could use a switch statement, but inevitably, we'll have nesting.
 * Our recursive/gen functions will iterate over this object and get at our functional intent.
 */

const generateMessagePageActions =
  (additionalActions: MessagingActions) =>
  (props: PageProps, attributes: PageAttributesAndActions): Actions => {
    const {containerId, iframeId, message = 'height', setPageLoaded} = attributes;
    /* Stores: in vanilla js, get it the vanilla way */
    const {resetAllMenus} = useNavigationStore.getState().actions;
    /*
    This function returns an object with functions that will execute in response to receiving a postMessage from
    the iframe. These are the common set of functions that will be provided to all instances of IframeView.

    Because each implementation is different you may need to add or overwrite some of the functions here (e.g., the basic loaded() function
    does not fire any segment events, but you want to add that functionality and overwrite the base loaded() function). In these cases you
    can pass a function that returns an object that contains your custom functions and the 2 objects will be merged together.

    Example:

    const myCustomStuff = (props, attributes) => {
      return {
        loaded() {
          console.log('do custom stuff here', props, attributes)
          attributes.setPageLoaded(true);
        }
        myCustomFunc() {
          // do custom stuff here
        }
      }
    }
    ...
    <IframeView additionalActions={myCustomStuff} />

  */
    const additional = additionalActions ? additionalActions(props, attributes) : {};
    const baseActions = {
      loaded() {
        setPageLoaded(true);
      },
      height() {
        /* Get updated elem references */
        const iframeCategoryContainer = document.getElementById(containerId);
        const iframeCategoryPage = document.getElementById(iframeId);

        if (iframeCategoryContainer) {
          iframeCategoryContainer!.style.height = `${message}px`;
          iframeCategoryPage!.style.height = `${message}px`;
          iframeCategoryPage!.style.width = '100%';
        }
      },
      anchorScrollBy() {
        window.scrollTo(0, message);
      },
      captureBodyClickEvent() {
        resetAllMenus();
      },
      /*
        This reads in the message from the type of 'goToLocation'. The message must be a
        valid url else it's gonna link to something bad.
       */
      goToLocation() {
        const pathName = pathnameFromString(message);
        Router.push(pathName);
      },
      /*
        This links to an external source, rather than internal to the app.
        They will either replace the current window, or "open a new tab" ie: newTab?

        For this method, "message" is an object:
        message: { locationURL, newTab }
     */
      goToExternalLocation() {
        const {newTab = false, locationURL} = message;

        if (newTab) {
          window.open(locationURL, '_blank');
        } else {
          window.location = locationURL;
        }
      },
      /**
       *
       * This method was created to fire a Segment event, and then push to new pathing.
       *
       * @remark
       * Pathing needs to be full url (webflow requires this), so we will chomp off what
       * we need and forward. Also, there is NO 'segment' SLA so, at this point, we just
       * consume the 'event' and 'action' on it, BUT we'd need to update at some point based on
       * segment conventions communicating to the iframe. Ie, does iframe pass us the full meta object,
       * or do we just get an event, and we build the meta object.
       *
       * Iframe calls this method.
       * @example
       * Usage:
       * postMessageToParent(
       *   { type: 'genericLinkAndTrackClick', message: {redirectUrl: string, segmennt: {event: string} }}
       * )
       * */
      genericLinkAndTrackClick() {
        /* Design will pass full urls. But we have to redirect on the environment level */
        const pathName = pathnameFromString(get(message, 'redirectUrl', ''));

        /*
          @remark
          We need to pass the event to the Segment API.
          Webflow needs to tell us what they want to track. Lets think of this as
          an API. The contract is:
          1. Pass the trackingType - this is the event name, top level
          2. Pass the event - this is the event name, second level
          3. Pass the properties - the properties is basically the "payload" so they need to format it.
         */
        const event = get(message, 'segment.event', '');
        const properties = get(message, 'segment.properties', {});
        const trackingType = get(message, 'segment.trackingType', TRACKING_TYPES.default);

        /* Fire Segment event */
        SegmentTrackingAction.default({event, properties}, trackingType);

        /* Push to new pathing */
        Router.push(pathName);
      },
    };
    return {...baseActions, ...additional};
  };

export {generateMessagePageActions};
